import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Card, TextInput } from "@opendate-app/setlist";
import TurnstileChallenge from "../../shared/TurnstileChallenge";
import TurnstileDisclosure from "../../shared/TurnstileDisclosure";
import { formatCents } from "../GiftCardProducts/giftCardFormat";
import { formatDate } from "../GiftCards/giftCardLabels";
import { postJson } from "../MyGiftCards/myGiftCardsRequests";
import "./styles/giftCardBalanceCheck.css";

export const NOT_FOUND_MESSAGE = "We couldn't find an active gift card with that code. Check the code and try again.";
export const RATE_LIMITED_MESSAGE = "Too many attempts. Wait a few minutes and try again.";
export const VERIFICATION_MESSAGE = "We couldn't verify this request. Please try again.";
export const MISSING_CODE_MESSAGE = "Enter the code from your gift card.";
export const UNAVAILABLE_MESSAGE = "Balance checks aren't available right now. Please try again.";

const failureFor = (status, data) => {
  if (status === 429 || data.status === "rate_limited") return { color: "warning", message: RATE_LIMITED_MESSAGE };
  if (data.status === "not_found") return { color: "error", message: NOT_FOUND_MESSAGE };
  if (data.status === "missing_code") return { color: "error", message: MISSING_CODE_MESSAGE };
  if (data.status === "captcha_required" || data.errors) return { color: "error", message: VERIFICATION_MESSAGE };
  return { color: "error", message: UNAVAILABLE_MESSAGE };
};

const BalanceResult = ({ result, onReset }) => (
  <Card padding="md">
    <div className="gift-card-balance-result">
      <div className="gift-card-balance-result-brand">{result.brandName}</div>
      <div className="gift-card-balance-result-last4">Gift card •••• {result.last4}</div>
      <div className="gift-card-balance-result-amount">{formatCents(result.balanceCents, result.currency)}</div>
      <div className="gift-card-balance-result-detail">
        {result.expiresAt ? `Expires ${formatDate(result.expiresAt)}` : "Never expires"}
      </div>
      <div className="gift-card-balance-result-detail">{result.whereItWorks}</div>
      <Button variant="secondary" onClick={onReset}>
        Check another card
      </Button>
    </div>
  </Card>
);

BalanceResult.propTypes = {
  result: PropTypes.shape({
    brandName: PropTypes.string.isRequired,
    last4: PropTypes.string.isRequired,
    balanceCents: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    expiresAt: PropTypes.string,
    whereItWorks: PropTypes.string.isRequired,
  }).isRequired,
  onReset: PropTypes.func.isRequired,
};

const GiftCardBalanceCheck = ({ balanceChecksPath, turnstileSiteKey, captchaRequired: initialCaptchaRequired }) => {
  const turnstileRef = useRef(null);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState(null);
  const [captchaRequired, setCaptchaRequired] = useState(Boolean(initialCaptchaRequired));
  const [submitting, setSubmitting] = useState(false);

  const turnstileToken = () => {
    if (!captchaRequired || !turnstileRef.current) return Promise.resolve("");
    return turnstileRef.current.executeAsync();
  };

  const check = async (event) => {
    event.preventDefault();
    setFailure(null);
    setSubmitting(true);
    try {
      const token = await turnstileToken();
      const { ok, status, data } = await postJson(balanceChecksPath, { gift_card_code: code, recaptcha_token: token });
      setCaptchaRequired((current) => current || Boolean(data.captchaRequired));
      if (ok) {
        setResult(data);
        setCode("");
      } else {
        setFailure(failureFor(status, data));
      }
    } catch (_error) {
      setFailure({ color: "error", message: VERIFICATION_MESSAGE });
    } finally {
      if (turnstileRef.current) turnstileRef.current.reset();
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setFailure(null);
  };

  return (
    <div className="gift-card-balance-check">
      {result ? (
        <BalanceResult result={result} onReset={reset} />
      ) : (
        <Card padding="md">
          <form className="gift-card-balance-form" onSubmit={check}>
            <div>
              <h1 className="gift-card-balance-title">Check a gift card balance</h1>
              <p className="gift-card-balance-subtitle">Enter the 16-character code from your gift card.</p>
            </div>
            {failure && <Alert color={failure.color} role="alert" title={failure.message} />}
            <TextInput
              id="gift-card-balance-code"
              label="Gift card code"
              placeholder="XXXX-XXXX-XXXX-XXXX"
              autoComplete="off"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            <Button type="submit" variant="primary" disabled={submitting || code.trim().length === 0}>
              Check balance
            </Button>
            {captchaRequired && (
              <>
                <TurnstileChallenge ref={turnstileRef} sitekey={turnstileSiteKey} />
                <TurnstileDisclosure />
              </>
            )}
          </form>
        </Card>
      )}
    </div>
  );
};

GiftCardBalanceCheck.propTypes = {
  balanceChecksPath: PropTypes.string.isRequired,
  turnstileSiteKey: PropTypes.string,
  captchaRequired: PropTypes.bool,
};

export default GiftCardBalanceCheck;
